import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import { CONTACTS_LOADING } from "../../../constants/loading.constant";
import {
  CONTACT_EXPAND_CREATION_FORM,
  CONTACT_IMPORT_EXPORT,
} from "../../../constants/modal.constant";
import { useContactCore } from "../../../cores/contacts";
import { ContactSourceOptions } from "../../../cores/contacts/config";
import { ContactSource } from "../../../cores/contacts/enum/ContactSource.enum";
import { Contact as CreatedContact } from "../../../cores/contacts/interfaces/api/get-contact";
import { Contact } from "../../../cores/contacts/interfaces/api/get-contacts";
import { useGlobalCore } from "../../../cores/globals";
import { Position } from "../../../cores/globals/interfaces/globalEntity";
import {
  MessageSchedulingChannel,
  ScheduledMessageContact,
  contactPhone,
} from "../../utils/messageSchedulingStorage";
import { InputWembii } from "../InputWembii";
import { LoadingSpinnerWembii } from "../LoadingSpinnerWembii";
import CreateContactForm from "../contacts/modal/CreateContactForm";
import { PaginationWemmbii } from "../pagination/Pagination";

const SEARCH_FIELDS = [
  { value: "name__p", label: "app.contacts.name" },
  { value: "email__p", label: "app.contacts.email" },
  { value: "tel__p", label: "app.contacts.phone" },
  { value: "reference__p", label: "app.contacts.reference" },
];

interface Props {
  companyId: string;
  channel: MessageSchedulingChannel;
  selected: ScheduledMessageContact[];
  onChange: (selected: ScheduledMessageContact[]) => void;
}

function contactLabel(contact: Contact): string {
  return contact.name || contact.email || contact.reference || contact.id;
}

function initials(value: string): string {
  return value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export const ContactsStep = (props: Props) => {
  const { companyId, channel, selected, onChange } = props;
  const intl = useIntl();
  const { contacts, total, getContacts, getContactTags } = useContactCore();
  const { turnOnModal, isModal } = useGlobalCore();

  const [searchField, setSearchField] = useState("name__p");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [onlyWithPhone, setOnlyWithPhone] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [reloadToken, setReloadToken] = useState(0);
  const importWasOpen = useRef(false);

  const debouncedSearch = useRef(
    _.debounce((value: string) => setSearch(value), 450)
  ).current;

  const phoneRequired = channel === "whatsapp";
  const phoneOnly = phoneRequired || onlyWithPhone;

  const refreshContacts = useCallback(() => {
    if (!companyId) return;
    getContacts(companyId, {
      page,
      perPage,
      [searchField]: search || undefined,
      owner: "",
      sources: source ? [source] : [],
      companies: [],
    });
  }, [companyId, getContacts, page, perPage, search, searchField, source]);

  useEffect(() => {
    debouncedSearch(searchDraft);
  }, [searchDraft, debouncedSearch]);

  useEffect(() => {
    refreshContacts();
  }, [refreshContacts, reloadToken]);

  const sourceOptions = useMemo(
    () => [
      { label: intl.formatMessage({ id: "app.source.all" }), value: "" },
      ...ContactSourceOptions.map((option) => ({
        value: String(option.value),
        label: intl.formatMessage({ id: option.label }),
      })),
    ],
    [intl]
  );

  const visibleContacts = useMemo(() => {
    const list: Contact[] = Array.isArray(contacts) ? contacts : [];
    if (!phoneOnly) return list;
    return list.filter((contact) => Boolean(contactPhone(contact)));
  }, [contacts, phoneOnly]);

  const selectedIds = useMemo(
    () => new Set(selected.map((item) => item.id)),
    [selected]
  );

  const hasActiveFilters = Boolean(search || source || (!phoneRequired && onlyWithPhone));

  const importOpen = isModal(CONTACT_IMPORT_EXPORT);

  useEffect(() => {
    if (importWasOpen.current && !importOpen) {
      setPage(1);
      setReloadToken((token) => token + 1);
    }
    importWasOpen.current = importOpen;
  }, [importOpen]);

  const toContactEntry = useCallback(
    (contact: Contact | CreatedContact): ScheduledMessageContact => ({
      id: contact.id,
      name: contactLabel(contact as Contact),
      email: contact.email || undefined,
      phone: contactPhone(contact) || undefined,
    }),
    []
  );

  const toggleContact = useCallback(
    (contact: Contact) => {
      if (selectedIds.has(contact.id)) {
        onChange(selected.filter((item) => item.id !== contact.id));
        return;
      }
      if (phoneRequired && !contactPhone(contact)) {
        toast.error(
          intl.formatMessage({
            id: "app.messageScheduling.contacts.phoneRequired",
          })
        );
        return;
      }
      onChange([...selected, toContactEntry(contact)]);
    },
    [intl, onChange, phoneRequired, selected, selectedIds, toContactEntry]
  );

  const selectPage = useCallback(() => {
    const eligible = visibleContacts.filter(
      (contact) =>
        !selectedIds.has(contact.id) &&
        (!phoneRequired || Boolean(contactPhone(contact)))
    );
    if (!eligible.length) return;
    onChange([...selected, ...eligible.map(toContactEntry)]);
  }, [
    onChange,
    phoneRequired,
    selected,
    selectedIds,
    toContactEntry,
    visibleContacts,
  ]);

  const clearFilters = useCallback(() => {
    setSearchDraft("");
    setSearch("");
    setSource("");
    setOnlyWithPhone(false);
    setSearchField("name__p");
    setPage(1);
  }, []);

  const onCreateContact = useCallback(() => {
    getContactTags(companyId);
    turnOnModal(
      CONTACT_EXPAND_CREATION_FORM,
      <CreateContactForm
        source={ContactSource.MANUAL}
        onCreation={(created) => {
          const entry = toContactEntry(created);
          if (phoneRequired && !entry.phone) {
            toast.info(
              intl.formatMessage({
                id: "app.messageScheduling.contacts.createdWithoutPhone",
              })
            );
          } else if (!selectedIds.has(created.id)) {
            onChange([...selected, entry]);
          }
          setPage(1);
          setReloadToken((token) => token + 1);
        }}
      />,
      Position.LEFT
    );
  }, [
    companyId,
    getContactTags,
    intl,
    onChange,
    phoneRequired,
    selected,
    selectedIds,
    toContactEntry,
    turnOnModal,
  ]);

  const onImportContacts = useCallback(() => {
    getContactTags(companyId);
    turnOnModal(CONTACT_IMPORT_EXPORT, true);
  }, [companyId, getContactTags, turnOnModal]);

  return (
    <div className="ms-contacts">
      <div className="ms-contacts__filters">
        <InputWembii
          type="text"
          mb={false}
          className="ms-contacts__search"
          placeholder={intl.formatMessage({
            id: "app.messageScheduling.contacts.search",
          })}
          value={searchDraft}
          onChange={(value: string) => {
            setSearchDraft(value);
            setPage(1);
          }}
          icon={<FontAwesomeIcon icon={["fas", "search"]} color="#94a3b8" />}
          prepend={
            <InputWembii
              type="select"
              mb={false}
              value={searchField}
              style={{ borderRadius: "10px 0 0 10px" }}
              onChange={(value: string) => {
                setSearchField(value);
                setPage(1);
              }}
            >
              {SEARCH_FIELDS.map((field) => (
                <option key={field.value} value={field.value}>
                  {intl.formatMessage({ id: field.label })}
                </option>
              ))}
            </InputWembii>
          }
        />

        <InputWembii
          type="select"
          mb={false}
          className="ms-contacts__source"
          label={intl.formatMessage({
            id: "app.messageScheduling.contacts.source",
          })}
          value={source}
          onChange={(value: string) => {
            setSource(value);
            setPage(1);
          }}
        >
          {sourceOptions.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </InputWembii>
      </div>

      <div className="ms-contacts__quickFilters">
        <button
          type="button"
          className={`ms-pill ${phoneOnly ? "is-active" : ""} ${
            phoneRequired ? "is-locked" : ""
          }`}
          disabled={phoneRequired}
          onClick={() => setOnlyWithPhone((prev) => !prev)}
        >
          <FontAwesomeIcon icon={["fas", "phone"]} />
          {intl.formatMessage({
            id: "app.messageScheduling.contacts.onlyWithPhone",
          })}
          {phoneRequired && (
            <FontAwesomeIcon icon={["fas", "lock"]} className="ms-pill__lock" />
          )}
        </button>
        <button type="button" className="ms-pill" onClick={selectPage}>
          <FontAwesomeIcon icon={["fas", "check-double"]} />
          {intl.formatMessage({
            id: "app.messageScheduling.contacts.selectPage",
          })}
        </button>
        <button type="button" className="ms-pill" onClick={clearFilters}>
          <FontAwesomeIcon icon={["fas", "rotate-left"] as any} />
          {intl.formatMessage({
            id: "app.messageScheduling.contacts.clearFilters",
          })}
        </button>
        <span className="ms-contacts__count">
          {intl.formatMessage(
            { id: "app.messageScheduling.contacts.totalFound" },
            { total: total || visibleContacts.length }
          )}
        </span>
      </div>

      {selected.length > 0 && (
        <div className="ms-selected">
          <div className="ms-selected__header">
            <span>
              {intl.formatMessage({
                id: "app.messageScheduling.contacts.selected",
              })}{" "}
              ({selected.length})
            </span>
            <Button color="link" className="p-0" onClick={() => onChange([])}>
              {intl.formatMessage({
                id: "app.messageScheduling.contacts.clearSelection",
              })}
            </Button>
          </div>
          <div className="ms-selected__chips">
            {selected.map((contact) => (
              <button
                key={contact.id}
                type="button"
                className="ms-selectedChip"
                onClick={() =>
                  onChange(selected.filter((item) => item.id !== contact.id))
                }
              >
                <span className="ms-selectedChip__avatar">
                  {initials(contact.name)}
                </span>
                <span className="ms-selectedChip__name">{contact.name}</span>
                {contact.phone && (
                  <span className="ms-selectedChip__phone">{contact.phone}</span>
                )}
                <FontAwesomeIcon icon={["fas", "xmark"] as any} />
              </button>
            ))}
          </div>
        </div>
      )}

      <LoadingSpinnerWembii id={CONTACTS_LOADING}>
        {visibleContacts.length === 0 ? (
          <div className="ms-empty ms-empty--contacts">
            <FontAwesomeIcon icon={["fas", "address-card"]} size="2x" />
            <span>
              {intl.formatMessage({
                id: hasActiveFilters
                  ? "app.messageScheduling.contacts.noResults"
                  : "app.messageScheduling.contacts.empty",
              })}
            </span>
            <p className="ms-empty__hint">
              {intl.formatMessage({
                id: "app.messageScheduling.contacts.emptyHint",
              })}
            </p>
            <div className="ms-empty__actions">
              <Button className="ms-btn ms-btn--primary" onClick={onCreateContact}>
                <FontAwesomeIcon icon={["fas", "user-plus"]} className="mr-2" />
                {intl.formatMessage({
                  id: "app.messageScheduling.contacts.create",
                })}
              </Button>
              <Button
                className="ms-btn ms-btn--ghost"
                onClick={onImportContacts}
              >
                <FontAwesomeIcon
                  icon={["fas", "file-import"]}
                  className="mr-2"
                />
                {intl.formatMessage({
                  id: "app.messageScheduling.contacts.import",
                })}
              </Button>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                className="ms-empty__clear"
                onClick={clearFilters}
              >
                {intl.formatMessage({
                  id: "app.messageScheduling.contacts.clearFilters",
                })}
              </button>
            )}
          </div>
        ) : (
          <div className="ms-contactList">
            {visibleContacts.map((contact) => {
              const phone = contactPhone(contact);
              const isSelected = selectedIds.has(contact.id);
              const disabled = phoneRequired && !phone;
              return (
                <button
                  key={contact.id}
                  type="button"
                  className={`ms-contactRow ${isSelected ? "is-selected" : ""} ${
                    disabled ? "is-disabled" : ""
                  }`}
                  onClick={() => toggleContact(contact)}
                >
                  <span className="ms-contactRow__check">
                    {isSelected && <FontAwesomeIcon icon={["fas", "check"]} />}
                  </span>
                  <span className="ms-contactRow__avatar">
                    {initials(contactLabel(contact))}
                  </span>
                  <span className="ms-contactRow__main">
                    <strong>{contactLabel(contact)}</strong>
                    <span className="ms-contactRow__meta">
                      {phone ? (
                        <span className="ms-contactRow__phone">
                          <FontAwesomeIcon icon={["fas", "phone"]} /> {phone}
                        </span>
                      ) : (
                        <span className="ms-contactRow__nophone">
                          {intl.formatMessage({
                            id: "app.messageScheduling.contacts.noPhone",
                          })}
                        </span>
                      )}
                      {contact.email && (
                        <span className="ms-contactRow__email">
                          <FontAwesomeIcon icon={["fas", "envelope"]} />{" "}
                          {contact.email}
                        </span>
                      )}
                    </span>
                  </span>
                  {contact.source && (
                    <span className="ms-chip ms-chip--sm">{contact.source}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </LoadingSpinnerWembii>

      <div className="ms-contacts__pagination">
        <PaginationWemmbii
          page={page}
          setPage={setPage}
          perPage={perPage}
          setPerPage={setPerPage}
          disabledPages={(contacts?.length ?? 0) < perPage}
        />
      </div>
    </div>
  );
};
